import { useEffect, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import {
    getLeadCalls,
    fetchCallRecording,
    startWebCall,
    endWebCall,
    cancelWebCall
} from '../../../api/supportAdmin';
import socket from '../../../utils/socket';
import './CallsTab.css';

const last10 = (v) => String(v || '').replace(/\D/g, '').slice(-10);

// Staff call history for one lead's phone number.
// The agent logs a call live: click Call → talk → End (with a note). After the
// call the system asks the customer to confirm it really happened, and the
// confirmation status shows on each call here.

const mins = (sec) => {
    if (!sec) return '0s';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const clock = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const whenText = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const OUTCOME_LABEL = {
    interested: 'Interested',
    booked: 'Booked',
    callback: 'Call back',
    not_interested: 'Not interested',
    no_answer: 'No answer',
    other: 'Other'
};

const OUTCOME_OPTIONS = [
    { value: '', label: 'Outcome (optional)' },
    { value: 'interested', label: 'Interested' },
    { value: 'booked', label: 'Booked' },
    { value: 'callback', label: 'Call back later' },
    { value: 'not_interested', label: 'Not interested' },
    { value: 'no_answer', label: "Didn't pick up" },
    { value: 'other', label: 'Other' }
];

// customer confirmation — "did our team really call you?"
const CONFIRM_BADGE = {
    pending: { t: 'Confirmation queued', c: 'cfMuted' },
    sent: { t: 'Awaiting customer reply', c: 'cfSent' },
    confirmed: { t: '✓ Customer confirmed call', c: 'cfYes' },
    denied: { t: '✕ Customer: no call received', c: 'cfNo' },
    no_response: { t: 'Customer did not reply', c: 'cfMuted' },
    skipped: { t: 'Confirmation not sent', c: 'cfMuted' },
    failed: { t: 'Confirmation failed to send', c: 'cfNo' }
};

export default function CallsTab({ user }) {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({ count: 0, outgoing: 0, answered: 0, missed: 0, lastAt: null });
    const [calls, setCalls] = useState([]);
    const [error, setError] = useState('');

    // live call being logged by this agent
    const [active, setActive] = useState(null); // { id, startedAt }
    const [elapsed, setElapsed] = useState(0);
    const [notes, setNotes] = useState('');
    const [outcome, setOutcome] = useState('');
    const [busy, setBusy] = useState(false);

    const number = user?.phoneNumber;
    const chatId = user?._id;

    const load = () => {
        if (!number) {
            setLoading(false);
            return Promise.resolve();
        }
        setError('');
        return getLeadCalls(number)
            .then((res) => {
                const d = res.data || {};
                setSummary({
                    count: d.count || 0,
                    outgoing: d.outgoing || 0,
                    answered: d.answered || 0,
                    missed: d.missed || 0,
                    lastAt: d.lastAt || null
                });
                setCalls(d.calls || []);
                // resume a live call left open by this agent
                setActive((cur) => {
                    if (cur) return cur;
                    const live = (d.calls || []).find((c) => c.live && c.source === 'web');
                    return live ? { id: live.id, startedAt: live.startedAt } : null;
                });
            })
            .catch(() => setError("Couldn't load call history"));
    };

    useEffect(() => {
        let gone = false;
        setLoading(true);
        load().finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [number]);

    // live updates — reload when a call for this lead changes (confirmation
    // sent, customer replied Yes/No, etc.) without a manual refresh.
    useEffect(() => {
        const myChat = String(chatId || '');
        const myNum = last10(number);
        const onCallUpdated = (p) => {
            if (!p) return;
            const matches =
                (p.chatId && p.chatId === myChat) || (p.phone10 && myNum && p.phone10 === myNum);
            if (matches) load();
        };
        socket.on('call-updated', onCallUpdated);
        return () => socket.off('call-updated', onCallUpdated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, number]);

    // tick the live timer
    useEffect(() => {
        if (!active) return;
        const base = new Date(active.startedAt).getTime();
        const tick = () => setElapsed(Math.max(0, Math.round((Date.now() - base) / 1000)));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [active]);

    const onStart = async () => {
        if (!chatId || busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await startWebCall(chatId);
            setActive({ id: res.data.id, startedAt: res.data.startedAt });
            setNotes('');
            setOutcome('');
        } catch {
            setError("Couldn't start the call");
        } finally {
            setBusy(false);
        }
    };

    const onEnd = async () => {
        if (!active || busy) return;
        setBusy(true);
        try {
            await endWebCall(active.id, { notes: notes.trim(), outcome });
            setActive(null);
            setNotes('');
            setOutcome('');
            await load();
        } catch {
            setError("Couldn't save the call");
        } finally {
            setBusy(false);
        }
    };

    const onCancel = async () => {
        if (!active || busy) return;
        setBusy(true);
        try {
            await cancelWebCall(active.id);
        } catch {
            /* ignore — maybe already removed */
        } finally {
            setActive(null);
            setNotes('');
            setOutcome('');
            setBusy(false);
            load();
        }
    };

    if (loading) {
        return (
            <div className='callsTab'>
                <div className='callsLoading'>
                    <ReactLoading type='spin' color='#006aff' height={28} width={28} />
                </div>
            </div>
        );
    }

    return (
        <div className='callsTab'>
            {/* ── Call action bar ── */}
            {!number ? (
                <div className='callActionBar callActionHint'>No phone number on this lead to call.</div>
            ) : active ? (
                <div className='callActive'>
                    <div className='callActiveHead'>
                        <span className='callLiveDot' /> On call · <strong>{clock(elapsed)}</strong>
                    </div>
                    <textarea
                        className='callNotesInput'
                        placeholder='What did you talk about? (notes)'
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                    />
                    <select className='callOutcomeSelect' value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                        {OUTCOME_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <div className='callActiveBtns'>
                        <button className='callEndBtn' onClick={onEnd} disabled={busy}>
                            ■ End call &amp; save
                        </button>
                        <button className='callCancelBtn' onClick={onCancel} disabled={busy}>
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <button className='callStartBtn' onClick={onStart} disabled={busy}>
                    📞 Start call
                </button>
            )}

            <div className='callsSummary'>
                <div className='callsStat'>
                    <b>{summary.count}</b>
                    <span>Total calls</span>
                </div>
                <div className='callsStat'>
                    <b>{summary.outgoing}</b>
                    <span>Outgoing</span>
                </div>
                <div className='callsStat'>
                    <b className='callsGreen'>{summary.answered}</b>
                    <span>Picked up</span>
                </div>
                <div className='callsStat'>
                    <b className='callsRed'>{summary.missed}</b>
                    <span>Missed</span>
                </div>
            </div>

            <div className='callsLastRow'>
                Last call: <strong>{whenText(summary.lastAt)}</strong>
            </div>

            {error ? (
                <div className='callsEmpty'>{error}</div>
            ) : calls.length === 0 ? (
                <div className='callsEmpty'>No calls to this number yet.</div>
            ) : (
                <div className='callsList'>
                    {calls
                        .filter((c) => !(active && c.id === active.id))
                        .map((c) => (
                            <CallItem key={c.id} call={c} />
                        ))}
                </div>
            )}
        </div>
    );
}

function ConfirmBadge({ status }) {
    if (!status || status === 'none') return null;
    const b = CONFIRM_BADGE[status];
    if (!b) return null;
    return <span className={`callConfirm ${b.c}`}>{b.t}</span>;
}

function CallItem({ call }) {
    const [audioUrl, setAudioUrl] = useState('');
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState('');
    const urlRef = useRef('');

    useEffect(() => {
        return () => {
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        };
    }, []);

    const loadAudio = () => {
        setAudioLoading(true);
        setAudioError('');
        fetchCallRecording(call.id)
            .then((res) => {
                const url = URL.createObjectURL(res.data);
                urlRef.current = url;
                setAudioUrl(url);
            })
            .catch(() => setAudioError("Couldn't load recording"))
            .finally(() => setAudioLoading(false));
    };

    const answered = call.answered;
    return (
        <div className='callItem'>
            <div className='callItemTop'>
                <div className='callItemDir'>
                    <span className={`callDirIcon callDir-${call.direction}`}>
                        {call.direction === 'incoming' ? '↙' : call.direction === 'missed' || call.direction === 'rejected' ? '✕' : '↗'}
                    </span>
                    <div>
                        <div className='callItemWhen'>{whenText(call.startedAt)}</div>
                        <div className='callItemMeta'>
                            {call.staffName ? `${call.staffName} · ` : ''}
                            {call.live
                                ? 'In progress…'
                                : answered
                                ? `Picked up · ${mins(call.durationSec)}`
                                : 'Not picked up'}
                        </div>
                    </div>
                </div>
                {call.outcome && <span className='callOutcome'>{OUTCOME_LABEL[call.outcome] || call.outcome}</span>}
            </div>

            {call.notes && <div className='callItemNotes'>{call.notes}</div>}

            <ConfirmBadge status={call.confirm} />

            <div className='callItemRec'>
                {call.hasRecording ? (
                    audioUrl ? (
                        <audio controls src={audioUrl} className='callAudio' />
                    ) : audioLoading ? (
                        <span className='callRecMuted'>Loading…</span>
                    ) : audioError ? (
                        <span className='callRecMuted'>{audioError}</span>
                    ) : (
                        <button className='callRecBtn' onClick={loadAudio}>
                            ▶ Play recording
                        </button>
                    )
                ) : call.source === 'web' ? null : (
                    <span className='callRecMuted'>
                        {call.recordingMissing ? 'No recording' : 'Recording pending'}
                    </span>
                )}
            </div>
        </div>
    );
}
